/**
 * Pure parts of drag and drop: typed payloads, hit geometry, reorder arithmetic, the keyboard-drag
 * reducers for lists and trees, and tree helpers. Shared by the pointer (Pragmatic drag and drop),
 * touch and keyboard paths, so all three drop at exactly the same place.
 */
import type { ClipboardDataOf, ClipboardPayload } from '../clipboard/payload';

/** Typed drag payloads (plan §8.11). */
export type DragKindMap = {
  records: { objectKey: string; ids: string[]; titles?: string[] };
  field: { key: string; objectKey?: string };
  block: { id: string; sourceId?: string };
  'palette-item': { kind: string; data?: Record<string, unknown> };
  'canvas-elements': { ids: string[]; sourceId?: string };
  widgets: { ids: string[] };
  page: { id: string };
  /** Library items (folders, pages, presentations, PDFs, dashboards) moved into a folder by drag. */
  'library-items': { items: { kind: string; id: string; title: string }[] };
  'ai-context': ClipboardDataOf<'ai-context'>;
  /**
   * A clipboard payload dragged as itself (a tool result, a copied chart): in-app targets take it like a
   * paste; with `external` data on the draggable, other tabs and apps get its native formats (dnd/data).
   */
  clipboard: ClipboardPayload;
  /** An item of a sortable list (`useSortableList`). */
  'list-item': { listId: string; key: string; index: number };
  /** A node of a tree (`useTreeDnd`, `useTreeDndForTree`); `ids` = the dragged block (selected siblings). */
  'tree-item': { treeId: string; id: string; ids?: string[] };
};

export type DragKind = keyof DragKindMap;

export type DragPayload<K extends DragKind = DragKind> = { [P in K]: { kind: P; data: DragKindMap[P] } }[K];

export type Edge = 'top' | 'bottom' | 'left' | 'right';
export type Axis = 'vertical' | 'horizontal';
export type Point = { x: number; y: number };
export type RectLike = { left: number; top: number; width: number; height: number };

/** The edge of `rect` closest to `point` among `edges`. */
export function closestEdge(rect: RectLike, point: Point, edges: readonly Edge[]): Edge | null {
  let best: Edge | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const edge of edges) {
    const d =
      edge === 'top'
        ? Math.abs(point.y - rect.top)
        : edge === 'bottom'
          ? Math.abs(rect.top + rect.height - point.y)
          : edge === 'left'
            ? Math.abs(point.x - rect.left)
            : Math.abs(rect.left + rect.width - point.x);
    if (d < bestDistance) {
      bestDistance = d;
      best = edge;
    }
  }
  return best;
}

export const axisEdges = (axis: Axis): Edge[] =>
  axis === 'vertical' ? ['top', 'bottom'] : ['left', 'right'];

/** Where an item that started at `from` lands when dropped on the `edge` of the item at `target`. */
export function reorderDestination(
  from: number,
  target: number,
  edge: Edge | null,
  axis: Axis = 'vertical',
): number {
  if (from === target || edge === null) return from;
  const after = axis === 'vertical' ? edge === 'bottom' : edge === 'right';
  const indexAfterRemoval = target > from ? target - 1 : target;
  return after ? indexAfterRemoval + 1 : indexAfterRemoval;
}

/** Move one element (immutable). */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= list.length) return [...list];
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(next.length, to)), 0, item!);
  return next;
}

// -- Keyboard drag of a list -------------------------------------------------------------------

export type ListKeyboardState =
  { phase: 'idle' } | { phase: 'lifted'; key: string; from: number; to: number; count: number };

export type ListKeyboardAction =
  | { type: 'lift'; key: string; index: number; count: number }
  | { type: 'move'; delta: number }
  | { type: 'moveTo'; index: number }
  | { type: 'drop' }
  | { type: 'cancel' };

export type ListKeyboardResult =
  | { type: 'lifted'; key: string; position: number; total: number }
  | { type: 'moved'; key: string; position: number; total: number }
  | { type: 'dropped'; key: string; from: number; to: number; total: number }
  | { type: 'cancelled'; key: string; from: number; total: number };

/**
 * The keyboard mode of every sortable list: Space lifts, arrows move, Space drops, Escape cancels.
 * Returns the next state and what happened (for the announcement and the reorder callback).
 */
export function listKeyboardReducer(
  state: ListKeyboardState,
  action: ListKeyboardAction,
): { state: ListKeyboardState; result?: ListKeyboardResult } {
  switch (action.type) {
    case 'lift': {
      if (action.count <= 0 || action.index < 0 || action.index >= action.count) return { state };
      const next: ListKeyboardState = {
        phase: 'lifted',
        key: action.key,
        from: action.index,
        to: action.index,
        count: action.count,
      };
      return {
        state: next,
        result: { type: 'lifted', key: action.key, position: action.index + 1, total: action.count },
      };
    }
    case 'move':
    case 'moveTo': {
      if (state.phase !== 'lifted') return { state };
      const target = action.type === 'move' ? state.to + action.delta : action.index;
      const to = Math.max(0, Math.min(state.count - 1, target));
      if (to === state.to) return { state };
      return {
        state: { ...state, to },
        result: { type: 'moved', key: state.key, position: to + 1, total: state.count },
      };
    }
    case 'drop': {
      if (state.phase !== 'lifted') return { state };
      return {
        state: { phase: 'idle' },
        result: { type: 'dropped', key: state.key, from: state.from, to: state.to, total: state.count },
      };
    }
    case 'cancel': {
      if (state.phase !== 'lifted') return { state };
      return {
        state: { phase: 'idle' },
        result: { type: 'cancelled', key: state.key, from: state.from, total: state.count },
      };
    }
  }
}

/** Keys in the order a lifted keyboard drag previews (the lifted item moves as you press arrows). */
export function previewOrder(keys: readonly string[], state: ListKeyboardState): string[] {
  if (state.phase !== 'lifted') return [...keys];
  const from = keys.indexOf(state.key);
  return from < 0 ? [...keys] : moveItem(keys, from, state.to);
}

// -- Trees ----------------------------------------------------------------------------------------

export type TreeNode = { id: string; children?: readonly TreeNode[] };

/** Where a node goes: under `parentId` (null = top level) at `index` among the siblings. */
export type TreeDestination = { parentId: string | null; index: number };

/** Drop instruction on a row: before it, after it, or inside it as its last child. */
export type TreeInstruction = 'before' | 'after' | 'inside';

export type FlatTreeRow = {
  id: string;
  parentId: string | null;
  depth: number;
  index: number;
  hasChildren: boolean;
};

/** Visible rows in order (collapsed nodes hide their children). */
export function flattenTree(
  nodes: readonly TreeNode[],
  expanded: (id: string) => boolean = () => true,
  parentId: string | null = null,
  depth = 0,
  out: FlatTreeRow[] = [],
): FlatTreeRow[] {
  nodes.forEach((node, index) => {
    const hasChildren = Boolean(node.children?.length);
    out.push({ id: node.id, parentId, depth, index, hasChildren });
    if (hasChildren && expanded(node.id)) flattenTree(node.children!, expanded, node.id, depth + 1, out);
  });
  return out;
}

export function findNode(nodes: readonly TreeNode[], id: string): TreeNode | undefined {
  for (const n of nodes) {
    if (n.id === id) return n;
    const inner = n.children ? findNode(n.children, id) : undefined;
    if (inner) return inner;
  }
  return undefined;
}

/** Is `id` the node `ancestor` or below it? (A node never moves into its own subtree.) */
export function isInSubtree(nodes: readonly TreeNode[], ancestor: string, id: string): boolean {
  const root = findNode(nodes, ancestor);
  if (!root) return false;
  const walk = (n: TreeNode): boolean => n.id === id || Boolean(n.children?.some(walk));
  return walk(root);
}

/** Parent and index of a node. */
export function locate(
  nodes: readonly TreeNode[],
  id: string,
  parentId: string | null = null,
): TreeDestination | null {
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i]!;
    if (n.id === id) return { parentId, index: i };
    const inner = n.children ? locate(n.children, id, n.id) : null;
    if (inner) return inner;
  }
  return null;
}

/** The destination of dropping `id` with `instruction` on row `targetId` (null when not allowed). */
export function treeDestination(
  nodes: readonly TreeNode[],
  id: string,
  targetId: string,
  instruction: TreeInstruction,
): TreeDestination | null {
  if (id === targetId || isInSubtree(nodes, id, targetId)) return null;
  if (instruction === 'inside')
    return { parentId: targetId, index: findNode(nodes, targetId)?.children?.length ?? 0 };
  const target = locate(nodes, targetId);
  const source = locate(nodes, id);
  if (!target || !source) return null;
  let index = instruction === 'before' ? target.index : target.index + 1;
  if (source.parentId === target.parentId && source.index < index) index -= 1;
  return { parentId: target.parentId, index };
}

/** Children of `parentId` (null = the roots). */
export function childrenOf(nodes: readonly TreeNode[], parentId: string | null): readonly TreeNode[] {
  return parentId === null ? nodes : (findNode(nodes, parentId)?.children ?? []);
}

/**
 * Where a block of nodes (`ids`, siblings in their order) lands when dropped with `instruction` on row
 * `targetId`: the new parent and the index of the first moved node among the parent's children after
 * the move (the `TreeMove` of the ui `Tree`). Null when the drop is not allowed (onto a moved node or
 * into its subtree) or would change nothing.
 */
export function treeBlockDestination(
  nodes: readonly TreeNode[],
  ids: readonly string[],
  targetId: string,
  instruction: TreeInstruction,
): TreeDestination | null {
  if (!ids.length || ids.some((id) => id === targetId || isInSubtree(nodes, id, targetId))) return null;
  const moved = new Set(ids);
  let dest: TreeDestination;
  if (instruction === 'inside') {
    const children = findNode(nodes, targetId)?.children ?? [];
    dest = { parentId: targetId, index: children.filter((n) => !moved.has(n.id)).length };
  } else {
    const target = locate(nodes, targetId);
    if (!target) return null;
    const siblings = childrenOf(nodes, target.parentId).filter((n) => !moved.has(n.id));
    const at = siblings.findIndex((n) => n.id === targetId);
    dest = { parentId: target.parentId, index: instruction === 'before' ? at : at + 1 };
  }
  return isNoopTreeMove(nodes, ids, dest) ? null : dest;
}

/** Would moving `ids` to `dest` leave the tree as it is? */
export function isNoopTreeMove(
  nodes: readonly TreeNode[],
  ids: readonly string[],
  dest: TreeDestination,
): boolean {
  const current = childrenOf(nodes, dest.parentId).map((n) => n.id);
  if (!ids.every((id) => current.includes(id))) return false;
  const rest = current.filter((id) => !ids.includes(id));
  const ordered = current.filter((id) => ids.includes(id));
  const after = [...rest.slice(0, dest.index), ...ordered, ...rest.slice(dest.index)];
  return after.every((id, i) => id === current[i]);
}

/** Apply a move (immutable). Nodes keep their other properties. */
export function moveTreeNode<N extends TreeNode>(
  nodes: readonly N[],
  id: string,
  dest: TreeDestination,
): N[] {
  let moved: N | undefined;
  const remove = (list: readonly N[]): N[] =>
    list
      .filter((n) => {
        if (n.id === id) {
          moved = n;
          return false;
        }
        return true;
      })
      .map((n) => (n.children ? ({ ...n, children: remove(n.children as N[]) } as N) : n));
  const without = remove(nodes);
  if (!moved) return [...nodes];
  const insertAt = (list: readonly N[]): N[] => {
    const next = [...list];
    next.splice(Math.max(0, Math.min(next.length, dest.index)), 0, moved!);
    return next;
  };
  const insert = (list: readonly N[]): N[] =>
    list.map((n) => {
      if (n.id === dest.parentId) return { ...n, children: insertAt((n.children ?? []) as N[]) } as N;
      if (n.children?.length) return { ...n, children: insert(n.children as N[]) } as N;
      return n;
    });
  return dest.parentId === null ? insertAt(without) : insert(without);
}

/** Row zone under the pointer: top quarter before, bottom quarter after, middle inside (when allowed). */
export function treeInstructionAt(rect: RectLike, point: Point, canNest: boolean): TreeInstruction {
  const rel = (point.y - rect.top) / Math.max(1, rect.height);
  if (!canNest) return rel < 0.5 ? 'before' : 'after';
  if (rel < 0.25) return 'before';
  if (rel > 0.75) return 'after';
  return 'inside';
}

export type TreeKeyboardState =
  { phase: 'idle' } | { phase: 'lifted'; id: string; origin: TreeDestination; dest: TreeDestination };

export type TreeKeyboardAction =
  | { type: 'lift'; id: string }
  | { type: 'up' }
  | { type: 'down' }
  | { type: 'indent' }
  | { type: 'outdent' }
  | { type: 'drop' }
  | { type: 'cancel' };

export type TreeKeyboardResult =
  | { type: 'lifted' | 'moved'; id: string; dest: TreeDestination }
  | { type: 'dropped'; id: string; from: TreeDestination; to: TreeDestination }
  | { type: 'cancelled'; id: string; from: TreeDestination };

/** Children of `parentId` (null = roots) without the lifted node. */
function siblingsWithout(nodes: readonly TreeNode[], parentId: string | null, id: string): TreeNode[] {
  const list = parentId === null ? nodes : (findNode(nodes, parentId)?.children ?? []);
  return list.filter((n) => n.id !== id);
}

/**
 * Keyboard mode of a tree: ↑/↓ move among the siblings (and past the ends into the neighbouring
 * level), → nests under the sibling above (as its last child), ← moves out after the parent.
 */
export function treeKeyboardReducer(
  nodes: readonly TreeNode[],
  state: TreeKeyboardState,
  action: TreeKeyboardAction,
  canNest: (parentId: string) => boolean = () => true,
): { state: TreeKeyboardState; result?: TreeKeyboardResult } {
  if (action.type === 'lift') {
    const origin = locate(nodes, action.id);
    if (!origin) return { state };
    return {
      state: { phase: 'lifted', id: action.id, origin, dest: origin },
      result: { type: 'lifted', id: action.id, dest: origin },
    };
  }
  if (state.phase !== 'lifted') return { state };
  const { id, dest } = state;
  const moved = (next: TreeDestination | null) =>
    next && (next.parentId !== dest.parentId || next.index !== dest.index)
      ? { state: { ...state, dest: next }, result: { type: 'moved' as const, id, dest: next } }
      : { state };
  const siblings = siblingsWithout(nodes, dest.parentId, id);
  switch (action.type) {
    case 'up':
      if (dest.index > 0) return moved({ parentId: dest.parentId, index: dest.index - 1 });
      if (dest.parentId !== null) {
        // Past the first child: before the parent.
        const parent = locate(nodes, dest.parentId);
        return moved(
          parent
            ? {
                parentId: parent.parentId,
                index: siblingsWithout(nodes, parent.parentId, id).findIndex((n) => n.id === dest.parentId),
              }
            : null,
        );
      }
      return { state };
    case 'down':
      if (dest.index < siblings.length) return moved({ parentId: dest.parentId, index: dest.index + 1 });
      if (dest.parentId !== null) {
        const parentSiblings = siblingsWithout(nodes, locate(nodes, dest.parentId)?.parentId ?? null, id);
        const at = parentSiblings.findIndex((n) => n.id === dest.parentId);
        return moved({ parentId: locate(nodes, dest.parentId)?.parentId ?? null, index: at + 1 });
      }
      return { state };
    case 'indent': {
      const above = siblings[dest.index - 1];
      if (!above || isInSubtree(nodes, id, above.id) || !canNest(above.id)) return { state };
      return moved({ parentId: above.id, index: (above.children ?? []).filter((n) => n.id !== id).length });
    }
    case 'outdent': {
      if (dest.parentId === null) return { state };
      const parent = locate(nodes, dest.parentId);
      if (!parent) return { state };
      const at = siblingsWithout(nodes, parent.parentId, id).findIndex((n) => n.id === dest.parentId);
      return moved({ parentId: parent.parentId, index: at + 1 });
    }
    case 'drop':
      return { state: { phase: 'idle' }, result: { type: 'dropped', id, from: state.origin, to: dest } };
    case 'cancel':
      return { state: { phase: 'idle' }, result: { type: 'cancelled', id, from: state.origin } };
  }
}
